import type { MatchConfig, MatchTeamSize, Team } from '../types';

export type MatchSetupMode = 'exhibition' | 'freeplay';

export type MatchSetupParticipant =
  | Readonly<{ kind: 'local'; instanceId: 'local' }>
  | Readonly<{ kind: 'bot'; instanceId: number; botId: string }>;

export interface MatchSetupState {
  readonly mode: MatchSetupMode;
  readonly teams: Readonly<Record<Team, readonly (MatchSetupParticipant | null)[]>>;
  readonly nextBotInstanceId: number;
}

const LOCAL_PARTICIPANT: MatchSetupParticipant = Object.freeze({ kind: 'local', instanceId: 'local' });
export const MATCH_SETUP_TEAM_CAPACITY = 3;

export function createMatchSetupState(defaultBotId?: string): MatchSetupState {
  let state = freezeState({
    mode: 'exhibition',
    teams: {
      blue: [LOCAL_PARTICIPANT, null, null],
      orange: [null, null, null],
    },
    nextBotInstanceId: 1,
  });
  if (defaultBotId && canPlaceBot(state, 'orange')) {
    state = placeBot(state, defaultBotId, 'orange', 0);
  }
  return state;
}

export function setMatchSetupMode(state: MatchSetupState, mode: MatchSetupMode): MatchSetupState {
  return mode === state.mode ? state : freezeState({ ...state, mode });
}

export function matchSetupLocalTeam(state: MatchSetupState): Team | null {
  for (const team of ['blue', 'orange'] as const) {
    if (state.teams[team].some((participant) => participant?.kind === 'local')) return team;
  }
  return null;
}

export function botAvailableForMatchSetup(state: MatchSetupState): boolean {
  return state.mode === 'exhibition';
}

export function canPlaceBot(state: MatchSetupState, team: Team): boolean {
  return botAvailableForMatchSetup(state) && (team === 'blue' || team === 'orange');
}

export function placeBot(
  state: MatchSetupState,
  botId: string,
  team: Team,
  slotIndex: number,
): MatchSetupState {
  assertSlot(state, team, slotIndex);
  if (!canPlaceBot(state, team)) return state;

  const teams = cloneTeams(state);
  teams[team][slotIndex] = Object.freeze({
    kind: 'bot',
    instanceId: state.nextBotInstanceId,
    botId,
  });
  return freezeState({ ...state, teams, nextBotInstanceId: state.nextBotInstanceId + 1 });
}

export function moveMatchSetupParticipant(
  state: MatchSetupState,
  sourceTeam: Team,
  sourceSlotIndex: number,
  targetTeam: Team,
  targetSlotIndex: number,
): MatchSetupState {
  assertSlot(state, sourceTeam, sourceSlotIndex);
  assertSlot(state, targetTeam, targetSlotIndex);
  if (sourceTeam === targetTeam && sourceSlotIndex === targetSlotIndex) return state;
  const participant = state.teams[sourceTeam][sourceSlotIndex];
  if (!participant) return state;

  const teams = cloneTeams(state);
  const target = teams[targetTeam][targetSlotIndex] ?? null;
  teams[targetTeam][targetSlotIndex] = participant;
  teams[sourceTeam][sourceSlotIndex] = target;
  return freezeState({ ...state, teams });
}

export function placeLocalPlayer(
  state: MatchSetupState,
  team: Team,
  slotIndex: number,
): MatchSetupState {
  assertSlot(state, team, slotIndex);
  if (matchSetupLocalTeam(state) !== null) return state;
  const teams = cloneTeams(state);
  teams[team][slotIndex] = LOCAL_PARTICIPANT;
  return freezeState({ ...state, teams });
}

export function removeMatchSetupParticipant(
  state: MatchSetupState,
  team: Team,
  slotIndex: number,
): MatchSetupState {
  assertSlot(state, team, slotIndex);
  if (!state.teams[team][slotIndex]) return state;
  const teams = cloneTeams(state);
  teams[team][slotIndex] = null;
  return freezeState({ ...state, teams });
}

export function matchSetupReady(state: MatchSetupState): boolean {
  if (state.mode === 'freeplay') return true;
  return matchSetupTeamSize(state) !== null;
}

export function matchSetupTeamSize(state: MatchSetupState): MatchTeamSize | null {
  if (state.mode !== 'exhibition') return null;
  const blue = occupiedCount(state.teams.blue);
  const orange = occupiedCount(state.teams.orange);
  if (blue !== orange || blue < 1 || blue > MATCH_SETUP_TEAM_CAPACITY) return null;
  return blue as MatchTeamSize;
}

export function matchConfigFromSetup(state: MatchSetupState): MatchConfig {
  if (state.mode === 'freeplay') return Object.freeze({ mode: 'freeplay' });
  const teamSize = matchSetupTeamSize(state);
  if (!teamSize) throw new Error('Both teams must have the same number of participants.');
  const localTeam = matchSetupLocalTeam(state);
  const bots: Array<Readonly<{ botId: string; team: Team }>> = [];
  for (const team of ['blue', 'orange'] as const) {
    for (const participant of state.teams[team]) {
      if (participant?.kind === 'bot') bots.push(Object.freeze({ botId: participant.botId, team }));
    }
  }
  return Object.freeze({ mode: 'exhibition', teamSize, localTeam, bots: Object.freeze(bots) });
}

function occupiedCount(slots: readonly (MatchSetupParticipant | null)[]): number {
  return slots.reduce((count, participant) => count + Number(participant !== null), 0);
}

function cloneTeams(state: MatchSetupState): Record<Team, Array<MatchSetupParticipant | null>> {
  return { blue: [...state.teams.blue], orange: [...state.teams.orange] };
}

function assertSlot(state: MatchSetupState, team: Team, slotIndex: number): void {
  if (!Number.isSafeInteger(slotIndex) || slotIndex < 0 || slotIndex >= state.teams[team].length) {
    throw new Error(`Match setup slot ${team}:${slotIndex} does not exist.`);
  }
}

function freezeState(state: {
  mode: MatchSetupMode;
  teams: Record<Team, readonly (MatchSetupParticipant | null)[]>;
  nextBotInstanceId: number;
}): MatchSetupState {
  return Object.freeze({
    ...state,
    teams: Object.freeze({
      blue: Object.freeze([...state.teams.blue]),
      orange: Object.freeze([...state.teams.orange]),
    }),
  });
}
